import classNames from "classnames";
import _ from "lodash";
import { twMerge } from "tailwind-merge";
import staticData from "../models.json";
import { customFieldNames, customTitles } from "./customisation";
import apiAgent from "../api/apiAgent";
import { SetStateAction, useAtomValue } from "jotai";
import { modelDataAtom } from "../atoms";

/**
 * delay function for better visual response
 * @param ms milliseconds
 * @returns
 */
export const pause = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * LWfn - Ensures the last input tailwind classes are merged. Can separate tailwind classes conditionally
 */
export const mergeClass = (...args: classNames.ArgumentArray) =>
  twMerge(classNames(args));

/**
 * LWfn - Get [from, id] params from URL Search Params as [string, string] or undefined
 */
export const useOriginParams = () => {
  const qryParams = new URLSearchParams(window.location.search);
  return [qryParams.get("from"), qryParams.get("id")];
};

/**
 * LWfn - Check if model has sortid
 */
export const useCheckSortable = (modelName: string) => {
  const modelData = useAtomValue(modelDataAtom);
  const findModel = modelData.find((md) => md.name === modelName);
  return _.has(findModel?.fields, "sortId");
};

/**
 * LWfn - Check Field Name against 'customFieldNames' constant.
 * returns [displayName:string, hint:string, required:boolean, accepts:string|undefined]
 * This list can be modified
 * @param {string} original is the name of the field
 */
export const checkFieldName = (
  original: string,
  modelName?: string
): [
  string,
  string | React.ReactNode | undefined,
  boolean,
  string | undefined,
  boolean | undefined
] => {
  const getFieldDetails = customFieldNames.find(
    (field) => field.original === original
  );

  const checkModel = getFieldDetails?.modelSpecific
    ? getFieldDetails?.modelSpecific?.find((m) => m.modelName === modelName)
    : undefined;

  const displayName = getFieldDetails?.display || _.capitalize(original);
  const hint = checkModel?.hint || getFieldDetails?.hint || undefined;
  const accept = checkModel?.accept || getFieldDetails?.accept || undefined;
  const optional = getBools(
    [checkModel?.optional, getFieldDetails?.optional],
    true
  );
  const hide = getBools([checkModel?.hide, getFieldDetails?.hide], false);

  return [displayName, hint, !optional, accept, hide];
};

/**
 * LWfn - Check Titles against 'customTitles' constant.
 * This list can be modified
 * @param {string} original is the name of the field
 */
export const checkTitle = (original: string): string => {
  const checkName = customTitles.find((field) => field.original === original);
  const displayName = checkName?.display || _.capitalize(original);
  return displayName;
};

/**
 * LWfn - Add order to model schema for forms. Compares data from /data/models to model.json in src folder
 * @param modelData accept json object from /data/models
 * @returns
 */
export const transformModelSchema = (modelData: any[]) => {
  const transformedData = modelData.map((model) => {
    // Match model name
    const modelName = model.name;
    const staticModel = staticData.find((model) => model.name === modelName);
    if (!staticModel) return model;

    // Get keys as ordered in local models.json
    const staticKeys = Object.keys(staticModel.fields);
    const initFields = staticKeys.reduce((obj, key: string) => {
      // make sure retrieved data has key
      const modelHasKey = key in model.fields;
      return modelHasKey ? { ...obj, [key]: model.fields[key] } : obj;
    }, {});

    // Get remaining keys no in models.json
    const dataKeys = Object.keys(model.fields);
    const combinedFields = dataKeys.reduce((obj, key: string) => {
      // make sure key has not been added to static field
      const staticHasKey = key in initFields;
      return staticHasKey
        ? initFields
        : { ...initFields, [key]: model.fields[key] };
    }, initFields);

    // TODO: Do order of relationships?

    return {
      ...model,
      fields: combinedFields,
    };
  });

  return transformedData;
};

/**
 * Use the provided function to pull the data from a table called "mediaItems" that has a "thumbUrl" or "fullImageUrl" column.
 * Will return undefined if any of the above does not exist
 * @returns (modelName:string, ownerId:number, setImageSrc:SetStateAction)
 */
export const useFetchThumbSrc = () => {
  /**
   * (modelName:string, ownerId:number, setImageSrc:SetStateAction)
   */
  return async (
    modelName: string,
    ownerId: number,
    setThumbSrc: React.Dispatch<SetStateAction<string | undefined>>
  ) => {
    if (modelName === "mediaItems") return;
    try {
      const response = await apiAgent.DataType.getAll("mediaItems");
      const allItems = response?.data || [];
      const ownerKey = `owner${modelName}Id`;
      const getFirst = allItems.find(
        (mi: any) => _.has(mi, ownerKey) && mi[ownerKey] === ownerId
      );
      if (getFirst)
        setThumbSrc(getFirst.thumbUrl || getFirst.fullImageUrl || undefined);
    } catch (err) {
      console.log(err);
    }
  };
};

/**
 * Removes HTML tags from string
 * @param str String to remove tags from
 * @returns
 */
export const removeTags = (str: string) => {
  return str.replace(/(<([^>]+)>)/gi, "");
};

/**
 * Check if string is a valid email
 * @param email string
 * @returns
 */
export const isValidEmail = (email: string) => {
  return /\S+@\S+\.\S+/.test(email);
};

/**
 * Convert timezone string to readable format
 * @param str_date timezone string
 * @returns
 */
export const parseDate = (str_date: string) => {
  return new Date(Date.parse(str_date)).toLocaleString();
};

/**
 * Checks through an array of booleans and returns the first one to be not undefined
 * @param array array of possible booleans
 * @param defaultAnswer default answer if all are undefined
 * @returns
 */
export const getBools = (
  array: any[],
  defaultAnswer?: boolean
): boolean | undefined => {
  const answer = array.find((item) => _.isBoolean(item));
  if (_.isBoolean(answer)) return answer;
  return defaultAnswer;
};
